import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { api, ApiError, WEB_API_BASE } from '../lib/api';
import type { CrudConfig } from './crudConfig';
import DateInput from '../components/DateInput';
import { formatDateLatam, parseLatamDate, isValidLatamDate } from '../lib/dates';

interface Item extends Record<string, unknown> {
  id?: string;
  _id?: string;
  title?: string;
}

const itemId = (it: Item, keyField?: string): string => {
  if (keyField) return String(it[keyField] ?? '');
  return String(it.id ?? it._id ?? '');
};

export default function CrudPage({ config }: { config: CrudConfig }) {
  const keyOf = useCallback((it: Item) => itemId(it, config.keyField), [config.keyField]);
  // Opciones de los campos `select`, cargadas del endpoint que declare cada uno.
  const [selectOptions, setSelectOptions] = useState<Record<string, { value: string; label: string }[]>>({});
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>(() => emptyValues(config));
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<{ url: string; file?: File } | null>(null);

  const reset = useCallback(() => {
    setEditingId(null);
    setValues(emptyValues(config));
    setImagePreview(null);
  }, [config]);

  const load = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const data = await api.get<Item[]>(config.listEndpoint);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : 'No se pudieron cargar los datos');
    } finally {
      setLoading(false);
    }
  }, [config.listEndpoint]);

  useEffect(() => {
    reset();
    load();
  }, [config, load, reset]);

  useEffect(() => {
    let cancelled = false;
    const selects = config.fields.filter(f => f.type === 'select' && f.options);
    if (selects.length === 0) {
      setSelectOptions({});
      return;
    }
    (async () => {
      const loaded: Record<string, { value: string; label: string }[]> = {};
      for (const field of selects) {
        const opt = field.options!;
        try {
          const rows = await api.get<Item[]>(opt.endpoint);
          loaded[field.name] = (Array.isArray(rows) ? rows : []).map(r => ({
            value: String(r[opt.valueField] ?? ''),
            label: String(r[opt.labelField] ?? r[opt.valueField] ?? ''),
          }));
        } catch {
          // Sin opciones el desplegable queda vacío; el resto del formulario
          // sigue siendo usable.
          loaded[field.name] = [];
        }
      }
      if (!cancelled) setSelectOptions(loaded);
    })();
    return () => { cancelled = true; };
  }, [config]);

  const startEdit = (item: Item) => {
    setEditingId(keyOf(item));
    const fromBackend = config.toFormValues(item);
    // Force date fields into dd/MM/yyyy for editing
    for (const f of config.fields) {
      if (f.type === 'date' && fromBackend[f.name]) {
        fromBackend[f.name] = formatDateLatam(fromBackend[f.name]);
      }
    }
    setValues({ ...emptyValues(config), ...fromBackend });
    if (fromBackend.image_url) {
      setImagePreview({ url: fromBackend.image_url });
    } else {
      setImagePreview(null);
    }
    setFeedback(null);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onChange = (name: string, v: string) => {
    setValues(prev => ({ ...prev, [name]: v }));
  };

  const handleImageUpload = useCallback(async (file: File) => {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setFeedback({ kind: 'error', text: 'Formato no válido (JPG/PNG/WEBP)' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setFeedback({ kind: 'error', text: 'Máximo 2 MB' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // POST /upload solo existe en el backend web (antes se llamaba por error
      // al de la app, que no tiene esa ruta). El token de admin-web es del
      // backend de la app, así que el backend web lo rechazará hasta que la
      // Fase 1 (login único) esté implementada; mientras tanto se puede pegar
      // la URL de la imagen manualmente en el campo de abajo.
      const res = await fetch(`${WEB_API_BASE}/upload`, {
        method: 'POST',
        body: fd,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_access_token') || ''}`,
        },
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new ApiError(
            'El backend web no acepta todavía la sesión de este panel (pendiente del login único). Pega la URL de la imagen manualmente.',
            res.status,
          );
        }
        throw new ApiError(`HTTP ${res.status}`, res.status);
      }
      const r = (await res.json()) as { url: string };
      const uploadedUrl = r?.url || '';
      setValues(prev => ({ ...prev, image_url: uploadedUrl }));
      setImagePreview({ url: uploadedUrl, file });
      setFeedback({ kind: 'success', text: 'Imagen subida' });
    } catch (err) {
      setFeedback({ kind: 'error', text: err instanceof ApiError ? err.message : 'No se pudo subir la imagen' });
    } finally {
      setUploading(false);
    }
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      // Validate latam dates before submission
      for (const f of config.fields) {
        if (f.type === 'date' && values[f.name] && !isValidLatamDate(values[f.name])) {
          setSaving(false);
          setFeedback({ kind: 'error', text: `La fecha de "${f.label}" debe tener formato dd/mm/aaaa` });
          return;
        }
      }
      // Normalize latam → ISO before passing to toPayload
      const normalized: Record<string, string> = { ...values };
      for (const f of config.fields) {
        if (f.type === 'date' && normalized[f.name]) {
          normalized[f.name] = parseLatamDate(normalized[f.name]);
        }
      }
      const payload = config.toPayload ? config.toPayload(normalized) : normalized;
      if (editingId) {
        await api.put(`/admin/${config.ctype}/${editingId}`, payload);
        setFeedback({ kind: 'success', text: 'Actualizado correctamente' });
      } else {
        await api.post<Item>(`/admin/${config.ctype}`, payload);
        setFeedback({ kind: 'success', text: 'Creado correctamente' });
      }
      reset();
      await load();
    } catch (err) {
      setFeedback({ kind: 'error', text: err instanceof ApiError ? err.message : 'Error al guardar' });
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (item: Item) => {
    const id = keyOf(item);
    if (!id) return;
    const ok = window.confirm(`¿Eliminar "${item.title ?? id}"?`);
    if (!ok) return;
    try {
      await api.del(`/admin/${config.ctype}/${id}`);
      setItems(prev => prev.filter(i => keyOf(i) !== id));
      if (editingId === id) reset();
    } catch (err) {
      setFeedback({ kind: 'error', text: err instanceof ApiError ? err.message : 'No se pudo eliminar' });
    }
  };

  const onMove = async (item: Item, direction: 'up' | 'down') => {
    const id = keyOf(item);
    if (!id) return;
    try {
      const res = await api.post<{ message?: string }>(`/admin/${config.ctype}/${id}/move`, { direction });
      await load();
      if (res?.message === 'Sin cambios') {
        setFeedback({ kind: 'success', text: 'Ya está en esa posición' });
      }
    } catch (err) {
      setFeedback({ kind: 'error', text: err instanceof ApiError ? err.message : 'No se pudo reordenar' });
    }
  };

  const sortedItems = useMemo(() => items, [items]);

  const edgesByCategory = useMemo(() => {
    const groups: Record<string, string[]> = {};
    for (const it of sortedItems) {
      const cat = String((it as Record<string, unknown>).category ?? '__all__');
      (groups[cat] ||= []).push(keyOf(it));
    }
    return groups;
  }, [sortedItems, keyOf]);

  return (
    <>
      <h1 className="page-title">{config.title}</h1>

      {feedback && (
        <div className={'alert ' + (feedback.kind === 'error' ? 'alert-error' : 'alert-success')}>
          {feedback.text}
        </div>
      )}

      <div className="card">
        <h2 className="section-title">{editingId ? `Editar ${config.singular}` : `Nuevo ${config.singular}`}</h2>
        <form onSubmit={onSubmit}>
          {config.fields.map(field => (
            <div key={field.name} className="field">
              <label htmlFor={field.name}>
                {field.label}{field.required ? ' *' : ''}
              </label>
              {field.name === 'image_url' ? (
                <div className="image-upload-section">
                  <div className="image-upload-inputs">
                    <div className="file-input-wrapper">
                      <input
                        type="file"
                        id={`${field.name}-file`}
                        accept="image/jpeg,image/png,image/webp"
                        onChange={e => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                        disabled={uploading}
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => document.getElementById(`${field.name}-file`)?.click()}
                        disabled={uploading}
                      >
                        {uploading ? 'Subiendo...' : 'Seleccionar imagen'}
                      </button>
                      <small className="muted" style={{ display: 'block', marginTop: 4 }}>
                        JPG, PNG o WEBP (máx 2 MB)
                      </small>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <small className="muted">O pega la URL manualmente:</small>
                      <input
                        type="url"
                        id={field.name}
                        required={field.required}
                        value={values[field.name] ?? ''}
                        onChange={e => onChange(field.name, e.target.value)}
                        placeholder="https://ejemplo.com/imagen.jpg"
                        style={{ marginTop: 4 }}
                      />
                    </div>
                  </div>
                  {imagePreview?.url && (
                    <div className="image-preview" style={{ marginTop: 12 }}>
                      <img src={imagePreview.url} alt="preview" style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: 4 }} />
                    </div>
                  )}
                </div>
              ) : field.type === 'textarea' ? (
                <textarea
                  id={field.name}
                  required={field.required}
                  value={values[field.name] ?? ''}
                  onChange={e => onChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                />
              ) : field.type === 'select' ? (
                <select
                  id={field.name}
                  required={field.required}
                  value={values[field.name] ?? ''}
                  onChange={e => onChange(field.name, e.target.value)}
                >
                  <option value="">{field.options?.emptyLabel ?? '— Sin asignar —'}</option>
                  {(selectOptions[field.name] ?? []).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : field.type === 'date' ? (
                <DateInput
                  id={field.name}
                  required={field.required}
                  value={values[field.name] ?? ''}
                  onChange={(v) => onChange(field.name, v)}
                  placeholder={field.placeholder || 'dd/mm/aaaa'}
                  testId={`date-${field.name}`}
                />
              ) : (
                <input
                  id={field.name}
                  type={field.type === 'number' ? 'number' : field.type === 'url' ? 'url' : 'text'}
                  required={field.required}
                  value={values[field.name] ?? ''}
                  onChange={e => onChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                />
              )}
              {field.helpText && <small className="muted">{field.helpText}</small>}
            </div>
          ))}
          <div className="btn-row">
            <button type="submit" className="btn" disabled={saving}>
              {saving ? 'Guardando…' : editingId ? 'Guardar cambios' : `Crear ${config.singular}`}
            </button>
            {editingId && (
              <button type="button" className="btn-ghost btn" onClick={reset}>Cancelar</button>
            )}
          </div>
        </form>
      </div>

      <div className="card">
        <h2 className="section-title">Existentes ({sortedItems.length})</h2>
        {loading ? <p className="muted">Cargando…</p> :
         listError ? <div className="alert alert-error">{listError}</div> :
         sortedItems.length === 0 ? <div className="empty">Sin {config.title.toLowerCase()} aún</div> :
          sortedItems.map(item => {
            const dateField = config.fields.find(f => f.type === 'date');
            const dateRaw = dateField ? (item as Record<string, unknown>)[dateField.name] : '';
            const dateText = dateRaw ? formatDateLatam(dateRaw) : '';
            const id = keyOf(item);
            const cat = String((item as Record<string, unknown>).category ?? '__all__');
            const group = edgesByCategory[cat] || [];
            const isFirst = group[0] === id;
            const isLast = group[group.length - 1] === id;
            return (
              <div key={id || Math.random()} className="list-item">
                <div className="list-item-title">
                  <div>{(item.title as string) || (item['reference'] as string) || '—'}</div>
                  {dateText && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{dateText}</div>}
                </div>
                <div className="list-item-actions">
                  {config.reorderable && (
                    <>
                      <button className="icon-btn" title="Subir" disabled={isFirst} onClick={() => onMove(item, 'up')}>↑</button>
                      <button className="icon-btn" title="Bajar" disabled={isLast} onClick={() => onMove(item, 'down')}>↓</button>
                    </>
                  )}
                  <button className="icon-btn" title="Editar" onClick={() => startEdit(item)}>✎</button>
                  <button className="icon-btn danger" title="Eliminar" onClick={() => onDelete(item)}>🗑</button>
                </div>
              </div>
            );
          })
        }
      </div>
    </>
  );
}

function emptyValues(config: CrudConfig): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of config.fields) out[f.name] = '';
  return out;
}
